import dayjs from "dayjs";
import { dateFromShortMonthYY } from "@package/grid-utils";
import * as ShortMonthYY from "@package/grid-utils/short-month-yy";

import { periodFromCellRef } from "../formatting/cell-ref";
import { rowIdToPeriod } from "../formatting/helpers";

/**
 * Calculate expiration date for a given period
 */
export function calculatePeriodExpiration(period: string): string | null {
  // Legacy alerts/rules store REL-* (and typed suffixes like REL-M0:mth).
  const resolved = rowIdToPeriod(period) || period;
  const cleanPeriod = resolved.replace(/\s+/g, "");

  // Handle months (e.g., "Feb26" or "MAR26" or "Feb 26"). Checked against the actual set of
  // month abbreviations (not a generic `[A-Z]{3}\d{2}` regex) since that also matches
  // non-month 3-letter{2-digit} codes like "Win26"/"Sum26"/"Cal26", which are handled below.
  if (ShortMonthYY.isCiShortMonthYY(cleanPeriod)) {
    const isoDate = dateFromShortMonthYY(ShortMonthYY.assertAs(cleanPeriod.toUpperCase()));
    return dayjs(isoDate).add(1, "month").toISOString();
  }

  // Handle quarters (e.g., "Q1 26" or "Q126"). Q1 ends March, Q2 June, Q3 September, Q4 December.
  if (/^Q[1-4]\d{2}$/i.test(cleanPeriod)) {
    const quarterNum = parseInt(cleanPeriod[1], 10);
    const year = parseInt(cleanPeriod.slice(-2), 10);
    const endMonth = quarterNum * 3;
    return dayjs(`${2000 + year}-${String(endMonth).padStart(2, "0")}-01`)
      .endOf("month")
      .toISOString();
  }

  // Handle years (e.g., "Cal 26" or "Cal26")
  if (/^Cal\d{2}$/i.test(cleanPeriod)) {
    const year = parseInt(cleanPeriod.slice(-2), 10);
    return dayjs(`${2000 + year}-12-01`)
      .endOf("month")
      .toISOString();
  }

  // Handle halves (e.g., "H1 26" or "H126")
  if (/^H[12]\d{2}$/i.test(cleanPeriod)) {
    const halfNum = parseInt(cleanPeriod[1], 10);
    const year = parseInt(cleanPeriod.slice(-2), 10);
    const endMonth = halfNum === 1 ? 6 : 12;
    return dayjs(`${2000 + year}-${String(endMonth).padStart(2, "0")}-01`)
      .endOf("month")
      .toISOString();
  }

  // Handle seasons (e.g., "Sum 26" or "Sum26", "Win 26" or "Win26").
  // Grid labels winter by the year it *starts* (Oct 26–Mar 27 is Win26), so
  // WinYY expires at the end of April in year YY+1.
  if (/^(Sum|Win)\d{2}$/i.test(cleanPeriod)) {
    const season = cleanPeriod.slice(0, 3).toLowerCase();
    const year = parseInt(cleanPeriod.slice(-2), 10);
    if (season === "sum") {
      return dayjs(`${2000 + year}-10-01`)
        .endOf("month")
        .toISOString();
    }
    return dayjs(`${2000 + year + 1}-04-01`)
      .endOf("month")
      .toISOString();
  }

  // Handle adhoc spreads - expire when ANY leg expires (earliest)
  // Formats: spread (FROM/TO), strip (FROM:TO), fly (FROM/MIDDLE/TO), condor (FROM/MIDDLE/MIDDLE2/TO)
  // Check if it's a strip format (uses : but not row_id format which ends with :mth, :qtr, etc.)
  const isStripFormat = cleanPeriod.includes(":") && !/:(mth|qtr|hlv|season|cal|adhoc)$/i.test(cleanPeriod);
  const isSpreadFormat = cleanPeriod.includes("/");

  if (isStripFormat || isSpreadFormat) {
    const separator = isStripFormat ? ":" : "/";
    const legs = cleanPeriod
      .split(separator)
      .map((leg) => leg.trim())
      .filter((leg) => leg && leg !== "-");
    const expirations = legs.map((leg) => calculatePeriodExpiration(leg)).filter(Boolean) as string[];
    if (expirations.length > 0) {
      return expirations.sort()[0]; // Return earliest expiration
    }
  }

  return null; // Unknown format
}

/** Whether an alert/rule with the given expired_at has expired relative to `now`. */
export function isExpiredAt(expiredAt: string | null | undefined, now: Date): boolean {
  return Boolean(expiredAt && new Date(expiredAt) <= now);
}

/** Whether the alert's own period/contract has passed relative to `now`. */
export function isAlertPeriodExpired(alert: { row_id?: string | null }, now: Date): boolean {
  const period = alert.row_id ? rowIdToPeriod(alert.row_id) : null;
  if (!period) return false;
  const periodExpiration = calculatePeriodExpiration(period);
  return Boolean(periodExpiration && new Date(periodExpiration) <= now);
}

/** Whether a cell-ref limit's period/contract has passed relative to `now`. */
export function isAlertLimitPeriodExpired(alert: { limit?: string | null }, now: Date): boolean {
  const referencedPeriod = periodFromCellRef(alert.limit ?? "");
  if (!referencedPeriod) return false;
  const periodExpiration = calculatePeriodExpiration(referencedPeriod);
  return Boolean(periodExpiration && new Date(periodExpiration) <= now);
}

/** Stored `expired_at`, or the contract-derived date when older rows left it null. */
export function resolveExpiredAt(alert: {
  row_id?: string | null;
  limit?: string | null;
  expired_at?: string | null;
}): string | null {
  if (alert.expired_at) return alert.expired_at;
  if (!alert.row_id) return null;
  return calculateExpiredAt(alert.row_id, alert.limit ?? "");
}

/**
 * Whether an alert is fully locked down (read-only, deletable only). Covers:
 * - the alert's own period/contract has passed ("Inactive")
 * - a cell-ref limit's period/contract has passed ("Inactive")
 * - `expired_at` has passed ("Expired")
 * Expired / contract-inactive alerts can only be inspected and deleted.
 */
export function isAlertFullyDisabled(
  alert: { row_id?: string | null; expired_at?: string | null; limit?: string | null },
  now: Date,
): boolean {
  return (
    isAlertPeriodExpired(alert, now) ||
    isAlertLimitPeriodExpired(alert, now) ||
    isExpiredAt(resolveExpiredAt(alert), now)
  );
}

/**
 * There's no dedicated column to record whether `expired_at` was auto-derived
 * from the alert's period/limit reference or fixed by the user via the
 * expiration picker. We infer "auto" by recomputing the period-based
 * expiration and comparing it (same calendar day) against the stored value.
 */
export function isAutoExpiredAt(
  expiredAt: string | null | undefined,
  period: string | null | undefined,
  limitRef: string | null | undefined,
): boolean {
  if (!expiredAt) return true;
  if (!period) return false;
  const auto = calculateExpiredAt(period, limitRef ?? "");
  return Boolean(auto) && dayjs(auto).isSame(expiredAt, "day");
}

/** Quick-pick fixed expiration dates, anchored to `now` (end of day so the alert doesn't expire immediately). */
export function presetExpirationDates(now: Date): { today: string; oneWeek: string; oneMonth: string } {
  return {
    today: dayjs(now).endOf("day").toISOString(),
    oneWeek: dayjs(now).add(1, "week").endOf("day").toISOString(),
    oneMonth: dayjs(now).add(1, "month").endOf("day").toISOString(),
  };
}

export type ExpirationChoice = "none" | "today" | "1w" | "1m" | "custom";

/** Classifies a stored `expired_at` against the auto value and the quick-pick presets, to pre-select/highlight the right option when the picker opens. */
export function classifyExpirationChoice(
  expiredAt: string | null | undefined,
  autoExpiredAt: string | null | undefined,
  now: Date,
): ExpirationChoice {
  if (!expiredAt) return "none";
  if (autoExpiredAt && dayjs(autoExpiredAt).isSame(expiredAt, "day")) return "none";

  const { today, oneWeek, oneMonth } = presetExpirationDates(now);
  if (dayjs(expiredAt).isSame(today, "day")) return "today";
  if (dayjs(expiredAt).isSame(oneWeek, "day")) return "1w";
  if (dayjs(expiredAt).isSame(oneMonth, "day")) return "1m";
  return "custom";
}

/**
 * Calculate expiration date considering both the period and any cell reference in the limit
 */
export function calculateExpiredAt(period: string, limitRef: string): string | null {
  // Calculate expiration for the alert's/rule's period (handles spread formulas automatically)
  const periodExpiration = calculatePeriodExpiration(period);

  // If limit is a cell reference, also calculate expiration for referenced period
  const referencedPeriod = periodFromCellRef(limitRef);
  const refExpiration = referencedPeriod ? calculatePeriodExpiration(referencedPeriod) : null;

  if (periodExpiration && refExpiration) {
    return dayjs(periodExpiration).isBefore(refExpiration) ? periodExpiration : refExpiration;
  }

  return periodExpiration ?? refExpiration ?? null;
}
